import { useRouteParams } from '@/context/routeParamsContext';
import * as dateTimeHelper from '@/helpers/dateTimeHelper';
import {
  prioritiseLounges,
  parseLoungeHoursMap,
  prioritiseAccessRules,
} from '@/helpers/loungeHelper';
import * as loungeHelper from '@/helpers/loungeHelper';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useSelector } from 'react-redux';
import {
  useIsDefaultLoungebuddy,
  useOneCmsLocalePartners,
  useOneCmsLocaleResultsOverrideWithPlaceholder,
  useOneCmsLocaleDetailsAmenities,
  useOneCmsLocaleDateFormattingObj,
} from '../oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  useBdaasAirportLounges,
  useOneCmsLocaleAirportLounges,
} from './loungesFetchyeHooks';

export const useTranslatedAirportLounges = () => {
  const isDefaultLoungeBuddy = useIsDefaultLoungebuddy();
  const partners = useOneCmsLocalePartners();
  const { locale } = useSelector(getLocaleData);
  const {
    data: bdaasLoungesResponse,
    error: bdaasLoungesError,
    isLoading: bdaasLoungesIsLoading,
  } = useBdaasAirportLounges();
  const {
    data: oneCmsLocaleLoungesResponse,
    error: oneCmsLocaleLoungesError,
    isLoading: oneCmsLocaleLoungesIsLoading,
  } = useOneCmsLocaleAirportLounges();

  const isLoading =
    (!bdaasLoungesResponse && bdaasLoungesIsLoading) || // check response because isLoading could be true (maybe due to caching)
    oneCmsLocaleLoungesIsLoading;

  if (isLoading) {
    return {
      data: null,
      error: bdaasLoungesError || oneCmsLocaleLoungesError,
      isLoading,
    };
  }

  let prioritisedLoungeMap = {};
  if (Array.isArray(bdaasLoungesResponse?.body)) {
    const prioritisedLounges = prioritiseLounges(
      locale,
      bdaasLoungesResponse?.body,
      partners
    );
    prioritisedLoungeMap = prioritisedLounges.reduce((acc, lounge) => {
      acc[lounge.id] = {
        ...lounge,
        hours: parseLoungeHoursMap(locale, lounge?.hours),
      };
      return acc;
    }, {});
  }
  const translatedLoungeMap = Object.entries(prioritisedLoungeMap).reduce(
    (acc, [loungeId, lounge]) => {
      const translatedLounge =
        oneCmsLocaleLoungesResponse?.body?.[loungeId] ?? null;
      if (translatedLounge) {
        acc[loungeId] = {
          ...lounge,
          name: translatedLounge?.name,
          description: translatedLounge?.description,
          location: {
            ...lounge?.location,
            terminal: translatedLounge?.terminal,
            terminal_directions: translatedLounge?.terminal_directions,
          },
        };
      } else if (isDefaultLoungeBuddy) {
        acc[loungeId] = lounge;
      }
      return acc;
    },
    {}
  );
  return {
    data: Object.values(translatedLoungeMap),
    error: bdaasLoungesError || oneCmsLocaleLoungesError,
    isLoading,
  };
};

export const useLoungeById = (loungeId) => {
  const lounges = useTranslatedAirportLounges();
  return lounges.data?.find((lounge) => lounge?.id === loungeId);
};

export const useIsLoungeOpen = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { locale } = useSelector(getLocaleData);
  return loungeHelper.isLoungeOpen(locale, lounge);
};

export const useLoungeAirportLinkUrl = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { product, airportCode } = useRouteParams();
  const airportCodeForLink = !lounge ? airportCode : lounge?.airport?.iata;

  return `${product}/${airportCodeForLink}`;
};

export const useLoungeDetailsCarouselImages = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const overrideWithPlaceholder =
    useOneCmsLocaleResultsOverrideWithPlaceholder();
  const oneCmsBaseImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  let images = [
    {
      imgUrl: `${oneCmsBaseImgUrl}placeholder.jpg`,
      imgId: 'placeholder',
    },
  ];
  if (lounge?.images?.length > 0 && !overrideWithPlaceholder) {
    images = lounge?.images.map((img) => ({
      imgUrl: img?.images?.full,
      imgId: img?.object_id,
    }));
  }
  return images;
};

export const useLoungeName = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.name;
};

export const useLoungeAirportIata = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.airport.iata;
};

export const useLoungeProviderId = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.access_rules[0].provider_id;
};

export const useLoungeDescription = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const firstNoteIdx = lounge?.description?.indexOf('NOTE');

  return lounge?.description?.slice(0, firstNoteIdx);
};

export const useLoungePrioritisedAccessRules = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const partners = useOneCmsLocalePartners();
  if (!lounge || !partners) {
    return null;
  }
  return prioritiseAccessRules(lounge?.access_rules, partners);
};

export const useLoungeAccessListMatchedWithLocaleProviders = (loungeId) => {
  const accessRules = useLoungePrioritisedAccessRules(loungeId);
  const marketProviders = useOneCmsLocalePartners();
  const defaultLoungeBuddy = useIsDefaultLoungebuddy();

  if (accessRules?.length > 0) {
    const loungePrimaryProviders =
      accessRules[0]?.provider_id?.split(',') ?? [];
    if (defaultLoungeBuddy) {
      return {
        id: accessRules[0]?.id,
        accessLevel: accessRules[0]?.access_requirements,
        guestPolicy: accessRules[0]?.guest_policy,
      };
    }
    const matchingProvider = marketProviders.find(
      ({ provider_id: providerId }) => {
        let parsedProviderList = providerId?.split(',') ?? [];
        parsedProviderList = parsedProviderList.map((provider) =>
          provider.trim()
        );
        return parsedProviderList.some((provider) =>
          loungePrimaryProviders.includes(provider)
        );
      }
    );
    if (matchingProvider) {
      return {
        id: matchingProvider?.provider_id,
        accessLevel: matchingProvider?.access_guests,
      };
    }
  }
  return {};
};

export const useLoungeAmenitiesWithLocaleTranslations = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const loungeAmenities = lounge?.amenities;
  const localeAmenities = useOneCmsLocaleDetailsAmenities(loungeId);
  return loungeAmenities.reduce((acc, loungeAmenity) => {
    const matchingLocaleAmenity = localeAmenities.find(
      (localeAmenity) => localeAmenity.name === loungeAmenity.id
    );
    if (matchingLocaleAmenity) {
      acc.push({
        // the ID must come from the lounges API so the market can't change it in their locale file
        id: loungeAmenity.id,
        label: matchingLocaleAmenity?.label,
      });
    }
    return acc;
  }, []);
};

export const useLoungeHoursInLocaleFormat = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const dateFormat = useOneCmsLocaleDateFormattingObj();
  const openingHours = lounge?.hours;
  if (loungeHelper.isNoteInsteadOfOpenCloseTimes(openingHours)) {
    return openingHours.monday[0].note;
  }
  return Object.keys(openingHours)
    .sort(
      (a, b) =>
        dateFormat.weekOrder?.indexOf(a) > dateFormat.weekOrder?.indexOf(b)
    )
    .map((day) => ({
      day,
      firstOpenTime: dateTimeHelper.parseMomentToString(
        openingHours[day][0].open,
        dateFormat.timeFormat
      ),
      firstCloseTime: dateTimeHelper.parseMomentToString(
        openingHours[day][0].close,
        dateFormat.timeFormat
      ),
      secondOpenTime: dateTimeHelper.parseTimeString(
        openingHours[day][1]?.open,
        dateFormat.timeFormat
      ),
      secondCloseTime: dateTimeHelper.parseTimeString(
        openingHours[day][1]?.close,
        dateFormat.timeFormat
      ),
    }));
};

export const useLoungeTerminal = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.location?.terminal;
};

export const useLoungeDirections = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.location?.terminal_directions;
};

export const useLoungeAirside = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.location?.airside;
};

export const useLoungeImportantInformation = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.important_information;
};
